import { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { BellOff, CheckCheck, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

// Pagina a cui porta ogni notifica; null = nessuna destinazione
function targetFor(n, role) {
  const mod = n.related_module;
  const id = n.related_entity_id;
  if (mod === 'client_orders') return `/client-orders${id ? `?open=${id}` : ''}`;
  if (mod === 'leave_requests') return '/leaves';
  if (mod === 'safety_points') return role === 'dipendente' || !id ? '/safety-points' : `/employees/${id}?tab=punti`;
  if (mod === 'inventory') return '/inventory';
  if (mod === 'oncall') return '/oncall';
  if (mod === 'regards') return '/regards';
  if (['scadenza_60gg', 'scadenza_30gg', 'scadenza_15gg', 'scadenza_oggi', 'scaduto'].includes(n.type)) return '/scadenze';
  return null;
}

export default function NotificationsPage() {
  const { fetchUnreadCount, user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    await api.put(`/notifications/${id}/read`);
    load();
    fetchUnreadCount();
  };

  const openNotification = async (n, to) => {
    if (!n.is_read) {
      try { await api.put(`/notifications/${n.id}/read`); fetchUnreadCount(); } catch { /* si naviga comunque */ }
    }
    navigate(to);
  };

  const markAllRead = async () => {
    await api.put('/notifications/read-all');
    load();
    fetchUnreadCount();
  };

  const severityIcon = (severity) => {
    if (severity === 'critical') return <div className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />;
    if (severity === 'warning') return <div className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" />;
    return <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />;
  };

  const typeLabel = (type) => {
    const m = {
      scadenza_60gg: 'Scadenza 60gg', scadenza_30gg: 'Scadenza 30gg',
      scadenza_15gg: 'Scadenza 15gg', scadenza_oggi: 'Scadenza Oggi',
      scaduto: 'Scaduto', alert_scorte: 'Scorte Basse',
      richiesta: 'Richiesta', info: 'Info', ordine: 'Ordine cliente',
    };
    return m[type] || type;
  };

  const unread = notifications.filter(n => !n.is_read);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="notifications-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Notifiche</h1>
          <p className="text-sm text-slate-500 mt-1">{unread.length} non lette su {notifications.length} totali</p>
        </div>
        {unread.length > 0 && (
          <Button variant="outline" onClick={markAllRead} data-testid="mark-all-read">
            <CheckCheck size={16} className="mr-1" /> Segna tutte lette
          </Button>
        )}
      </div>

      {notifications.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-12 text-center">
            <BellOff size={32} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">Nessuna notifica</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {notifications.map(n => {
            const to = targetFor(n, user?.role_name);
            return (
            <Card
              key={n.id}
              className={`border-slate-200 shadow-sm transition-all ${!n.is_read ? 'bg-blue-50/40 border-blue-200' : ''} ${to ? 'cursor-pointer hover:border-blue-400 hover:shadow-md' : ''}`}
              onClick={to ? () => openNotification(n, to) : undefined}
              data-testid={`notification-${n.id}`}
            >
              <CardContent className="p-4 flex items-start gap-3">
                {severityIcon(n.severity)}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-sm font-semibold text-slate-900">{n.title}</p>
                    <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-200 text-[10px]">{typeLabel(n.type)}</Badge>
                  </div>
                  <p className="text-sm text-slate-600">{n.message}</p>
                  <p className="text-xs text-slate-400 mt-1">{new Date(n.created_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</p>
                </div>
                {!n.is_read && (
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); markRead(n.id); }} className="text-blue-600 flex-shrink-0" data-testid={`read-${n.id}`}>
                    Letta
                  </Button>
                )}
                {to && <ArrowRight size={16} className="text-slate-400 flex-shrink-0 mt-0.5" aria-label="Apri" />}
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
